/**
 * PostHog Analytics Integration for Crypnal
 *
 * Self-hosted proxy to bypass ad blockers
 * Tracks: page views, feature usage, user interactions, web vitals, frustration
 */

import posthog from 'posthog-js';
import { getInstallContext } from './pwaDetection.js';
import { getDebugSnapshot } from './installPromptManager.js';

// PostHog configuration - use subdomain proxy to bypass adblockers
const POSTHOG_HOST = 'https://phg.tradermap.io'; // Proxied through subdomain
const POSTHOG_UI_HOST = 'https://eu.posthog.com'; // Dashboard stays on original
// SECURITY: API key must be configured via environment variable, no fallback
const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY;

let isInitialized = false;
// Events captured before posthog.init's async `loaded` callback fires would
// otherwise be silently dropped by `capture()`. PWA funnel events (the
// install banner mounts synchronously on first render) hit this race
// reliably. Cap the buffer so a misconfigured init doesn't leak memory.
const PENDING_CAPTURE_LIMIT = 100;
const pendingCaptures = [];

function flushPendingCaptures() {
  if (!isInitialized) return;
  while (pendingCaptures.length > 0) {
    const { event, properties } = pendingCaptures.shift();
    try { posthog.capture(event, properties); } catch { /* ignore */ }
  }
}

/**
 * Initialize PostHog analytics
 * Call this once on app startup
 */
export function initAnalytics() {
  if (isInitialized) return;

  // Don't initialize without a real API key
  if (!POSTHOG_API_KEY || POSTHOG_API_KEY === 'phc_placeholder') {
    console.log('[Analytics] PostHog not initialized - no API key configured');
    return;
  }

  try {
    posthog.init(POSTHOG_API_KEY, {
      api_host: POSTHOG_HOST,
      ui_host: POSTHOG_UI_HOST,
      // Capture page views automatically
      capture_pageview: true,
      // Capture page leaves for session duration
      capture_pageleave: true,
      // Enable session recording
      disable_session_recording: false,
      session_recording: {
        maskAllInputs: false,
        maskInputOptions: { password: true },
        recordCanvas: true,
        sampleRate: 1, // 100% of sessions
      },
      // Autocapture clicks, inputs, etc.
      autocapture: true,
      // Persist user ID across sessions
      persistence: 'localStorage',
      // Bootstrap with distinct_id if available
      bootstrap: {
        distinctID: getStoredUserId()
      },
      // Enable exception autocapture for error tracking
      autocapture_exceptions: true,
      // Drop benign browser noise before it reaches the PostHog ingestion pipeline.
      // stopImmediatePropagation() in main.jsx covers the addEventListener path;
      // before_send covers window.onerror (which ignores stopImmediatePropagation).
      before_send: (event) => {
        if (event?.event === '$exception') {
          const props = event.properties || {};
          const v0 = props.$exception_values?.[0];
          const l0 = props.$exception_list?.[0];
          // posthog-js has shipped the exception payload as both a bare string
          // and a { type, value } object across versions — gather every shape.
          const msg = [
            typeof v0 === 'string' ? v0 : v0?.value,
            l0?.value,
            l0?.type,
            props.$exception_message,
          ].filter(Boolean).join(' ');
          // Pure browser/extension noise that is not actionable from app code:
          // benign ResizeObserver churn, cross-origin "Script error." with no
          // stack, injected wallet-extension (MetaMask etc.) RPC errors — the
          // app ships no web3/wallet integration — plus reader-mode / dark-mode
          // / messaging shims that browsers and extensions inject at page load.
          // These dominated the daily error-triage "known noise" list.
          const NOISE = [
            'ResizeObserver loop',
            'Script error.',
            'Internal JSON-RPC error',
            'disconnected from all chains',
            '__firefox__',            // Firefox/iOS Safari reader-mode injection
            'DarkReader',             // Dark Reader extension
            'runtime.sendMessage',    // extension messaging bridge
            'Receiving end does not exist', // extension messaging bridge
            'Tab not found',          // extension messaging bridge
            'User rejected the request', // wallet extension prompt dismissal
            'selectedAddress',        // window.ethereum probe by wallet extension
            'evmAsk',                 // wallet extension injection
            "evaluating 'response.result'", // wallet extension RPC reading undefined response (019e9f8c)
            'standardSelectors',      // browser extension DOM selector injection (019e540d)
          ];
          if (NOISE.some((n) => msg.includes(n))) return null;

          // Drop anything thrown from a browser-extension / internal context.
          // Extension-injected scripts run under chrome-extension:// etc. and
          // their exceptions are never fixable from app code. Guarded so an
          // unexpected payload shape can never break ingestion.
          try {
            const frames = l0?.stacktrace?.frames || [];
            const fromExtension = frames.some((f) => {
              const src = f?.filename || f?.source || f?.abs_path || '';
              return (
                src.startsWith('chrome-extension://') ||
                src.startsWith('moz-extension://') ||
                src.startsWith('safari-web-extension://') ||
                src.startsWith('safari-extension://')
              );
            });
            if (fromExtension) return null;
          } catch { /* ignore — keep the event */ }
        }
        return event;
      },
      // PostHog client-side rate limiter — defaults are 10/sec, 100 burst,
      // which active users on /trades regularly hit (clicks on trade rows
      // + tooltip + price ticks all generate autocapture events). When
      // throttled, the SDK silently drops events and emits a server-side
      // client_ingestion_warning. Raise so we don't drop real activity.
      rate_limiting: {
        events_per_second: 50,
        events_burst_limit: 500,
      },
      // Custom properties for all events
      loaded: (posthogInstance) => {
        // Add global properties
        posthogInstance.register({
          app_version: '1.0.0',
          platform: 'web'
        });

        // Register PWA super-properties BEFORE draining the pending-capture
        // queue. InstallBanner mounts synchronously on first React render
        // and enqueues `pwa_banner_shown` before posthog finishes loading;
        // if super-properties aren't set first, that event lands without
        // `pwa_standalone`/`pwa_installed` and funnels filtered on those
        // exclude it. trackPwaSession (in 1s timer) re-registers later —
        // harmless overwrite with the same values.
        try {
          const ctx = getInstallContext();
          const snap = getDebugSnapshot();
          posthogInstance.register({
            pwa_standalone: ctx.standalone,
            pwa_installed: snap.installed,
          });
        } catch { /* ignore */ }

        console.log('[Analytics] PostHog initialized successfully (proxy mode)');
        isInitialized = true;

        // Drain anything captured between init kickoff and the loaded
        // callback resolving (e.g. pwa_banner_shown on first React render).
        flushPendingCaptures();

        // Set up global error handlers after PostHog is loaded
        setupGlobalErrorHandlers();

        // Set initial user properties
        setInitialUserProperties();

        // Set up engagement tracking (active/idle/hidden time)
        setupEngagementTracking();
      }
    });
  } catch (error) {
    console.error('[Analytics] Failed to initialize PostHog:', error);
  }
}

/**
 * Get or create a persistent user ID
 * Exported so WebSocket hooks can use the same userId
 * SECURITY: Uses crypto.randomUUID() for cryptographically secure ID generation
 */
export function getStoredUserId() {
  let userId = localStorage.getItem('crypnal_user_id');
  if (!userId) {
    // Use cryptographically secure random UUID
    userId = `user_${crypto.randomUUID()}`;
    localStorage.setItem('crypnal_user_id', userId);
  }
  return userId;
}

/**
 * Set up global error handlers for uncaught errors
 * Called after PostHog is initialized
 */
function setupGlobalErrorHandlers() {
  // Intentionally a no-op now: autocapture_exceptions:true already wires
  // window.error and unhandledrejection handlers in posthog-js using the
  // current $exception_list schema. Our previous manual handlers shipped
  // events without $exception_list and PostHog rejected ~10k/30d as
  // "missing field $exception_list" ingestion errors.
  console.log('[Analytics] Using autocapture_exceptions for global errors');
}

/**
 * Set initial user properties on load
 */
export function setInitialUserProperties() {
  if (!isInitialized) return;

  posthog.people.set({
    browser_language: navigator.language,
    screen_resolution: `${screen.width}x${screen.height}`,
    viewport_size: `${innerWidth}x${innerHeight}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    connection_type: navigator.connection?.effectiveType || 'unknown'
  });
}

/**
 * Manually capture an exception
 * Use this in catch blocks for important errors
 * @param {Error} error - The error object
 * @param {Object} context - Additional context
 */
export function captureException(error, context = {}) {
  if (!isInitialized) {
    console.error('[Error]', error.message, context);
    return;
  }

  // posthog-js >= 1.140 ships posthog.captureException() which produces
  // the modern $exception_list payload — the previous manual capture
  // was rejected server-side as "missing field $exception_list".
  posthog.captureException(error, {
    page: window.location.pathname,
    ...context,
  });
}

/**
 * Generic capture wrapper. Use when an event doesn't map to one of the
 * pre-defined helpers (e.g. `liqmap.viewed`). Safely no-ops when PostHog
 * isn't initialized.
 * @param {string} event - Event name
 * @param {Object} properties - Event properties
 */
export function capture(event, properties = {}) {
  if (!isInitialized) {
    if (pendingCaptures.length < PENDING_CAPTURE_LIMIT) {
      pendingCaptures.push({ event, properties });
    }
    return;
  }
  posthog.capture(event, properties);
}

/**
 * Register PWA super-properties so they ride along with every event.
 * Allows PostHog funnels/insights to filter the entire event stream by
 * "is this user running the installed PWA?" without needing a join.
 * Safe no-op when PostHog isn't initialized.
 * @param {{ pwa_standalone: boolean, pwa_installed: boolean }} props
 */
export function registerPwaSuperProperties(props) {
  if (!isInitialized) return;
  try {
    posthog.register(props);
  } catch { /* ignore */ }
}

// /chart dwell-time tracker. Persists a cumulative second-count in
// localStorage so it survives reloads / multiple sessions, mirrors it
// into the PostHog person property `tm_chart_seconds_total`, and emits
// one-shot lifecycle events the "Chart dashboards feedback" survey
// triggers on:
//   - chart_dashboards_engaged — fires once when the user crosses 10 min
//     of cumulative time on /chart (heavy user, ask what they like).
//   - chart_dashboards_visited — fires once when the user leaves /chart
//     having spent >=30s in this session but total still <10min. Neutral
//     name on purpose: a 5-minute happy user is NOT a bouncer, just a
//     short session. The survey's rating-branching figures out which
//     question to ask.
const ENGAGED_THRESHOLD_SEC = 600;
const VISIT_MIN_SESSION_SEC = 30;
// Gate keys are versioned (.v2). Bumping the suffix is the lever for
// re-issuing the survey trigger to users who already fired the event
// once and so wouldn't fire it again under the same flag. Bumped after
// 2026-05-17 when the phg.tradermap.io proxy was found to be 404-ing
// /api/surveys/ — every prior fire was wasted because the SDK couldn't
// load the survey definition. Counts are preserved in TOTAL_SECONDS_KEY.
const ENGAGED_FIRED_KEY = 'tm.chart.engagedFired.v2';
const VISITED_FIRED_KEY = 'tm.chart.visitedFired.v2';
const TOTAL_SECONDS_KEY = 'tm.chart.secondsTotal';

function readLocalInt(key) {
  try {
    const v = parseInt(localStorage.getItem(key) || '0', 10);
    return Number.isFinite(v) ? v : 0;
  } catch { return 0; }
}

function setLocalFlag(key) {
  try { localStorage.setItem(key, '1'); } catch { /* ignore */ }
}

function hasLocalFlag(key) {
  try { return localStorage.getItem(key) === '1'; } catch { return false; }
}

/**
 * Add `seconds` to the cumulative /chart dwell counter and sync to PostHog.
 * Fires `chart_dashboards_engaged` once when the counter crosses 10min.
 */
export function recordChartDwellSeconds(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return;
  const prev = readLocalInt(TOTAL_SECONDS_KEY);
  const next = prev + Math.round(seconds);
  try { localStorage.setItem(TOTAL_SECONDS_KEY, String(next)); } catch { /* ignore */ }
  if (!isInitialized) return;
  posthog.people.set({ tm_chart_seconds_total: next });
  // Fire when total dwell crosses the threshold AND the gate hasn't yet
  // been latched under the current gate version. The previous form gated
  // on "crossed this tick" (prev < T && next >= T), which had no way to
  // re-issue the event after a key bump — already-engaged users were
  // permanently silent. The current form re-fires once per key version
  // for any user whose accumulated dwell already meets the threshold.
  if (next >= ENGAGED_THRESHOLD_SEC && !hasLocalFlag(ENGAGED_FIRED_KEY)) {
    posthog.capture('chart_dashboards_engaged', { seconds_total: next });
    setLocalFlag(ENGAGED_FIRED_KEY);
  }
}

/**
 * Call on /chart unmount. If the user spent >=30s this session AND total
 * is still under the engagement threshold, emits `chart_dashboards_visited`
 * once. The neutral "visited" name covers the whole "tried it, then left"
 * spectrum — including the 5-minute happy user. Sub-30s sessions are
 * skipped: no signal to mine from accidental clicks.
 */
export function captureChartVisitIfApplicable(sessionSeconds) {
  if (!isInitialized) return;
  if (sessionSeconds < VISIT_MIN_SESSION_SEC) return;
  if (hasLocalFlag(VISITED_FIRED_KEY)) return;
  if (hasLocalFlag(ENGAGED_FIRED_KEY)) return;
  const total = readLocalInt(TOTAL_SECONDS_KEY);
  if (total >= ENGAGED_THRESHOLD_SEC) return;
  posthog.capture('chart_dashboards_visited', {
    session_seconds: sessionSeconds,
    seconds_total: total,
  });
  setLocalFlag(VISITED_FIRED_KEY);
}

/**
 * Track a page view (call on route changes)
 * @param {string} pageName - Name of the page (e.g., 'trades', 'hyperliquid')
 * @param {Object} properties - Additional properties
 */
export function trackPageView(pageName, properties = {}) {
  if (!isInitialized) return;

  posthog.capture('$pageview', {
    page_name: pageName,
    path: window.location.pathname,
    ...properties
  });
}

/**
 * Track when user changes a filter
 * @param {string} filterType - Type of filter (e.g., 'whale_threshold', 'liquidation_threshold', 'exchange', 'coin')
 * @param {any} value - New filter value
 * @param {Object} context - Additional context (page, previous value, etc.)
 */
export function trackFilterChange(filterType, value, context = {}) {
  if (!isInitialized) return;

  posthog.capture('filter_changed', {
    filter_type: filterType,
    value: value,
    page: context.page || window.location.pathname,
    ...context
  });
}

/**
 * Track when user clicks on a coin
 * @param {string} coin - Coin symbol (e.g., 'BTC', 'ETH')
 * @param {Object} context - Additional context (page, trade type, etc.)
 */
export function trackCoinClick(coin, context = {}) {
  if (!isInitialized) return;

  posthog.capture('coin_clicked', {
    coin: coin,
    page: context.page || window.location.pathname,
    ...context
  });
}

/**
 * Track when a modal is opened
 * @param {string} modalName - Name of the modal
 * @param {Object} properties - Additional properties
 */
export function trackModalOpen(modalName, properties = {}) {
  if (!isInitialized) return;

  posthog.capture('modal_opened', {
    modal: modalName,
    ...properties
  });
}

/**
 * Track when a modal is closed
 * @param {string} modalName - Name of the modal
 * @param {Object} properties - Additional properties (e.g., time spent)
 */
export function trackModalClose(modalName, properties = {}) {
  if (!isInitialized) return;

  posthog.capture('modal_closed', {
    modal: modalName,
    ...properties
  });
}

/**
 * Track WebSocket connection events
 * @param {string} type - Connection type (e.g., 'trades', 'hyperliquid', 'polymarket')
 * @param {string} status - Connection status (e.g., 'connected', 'disconnected', 'error')
 */
export function trackWebSocketConnection(type, status) {
  if (!isInitialized) return;

  posthog.capture('websocket_connection', {
    connection_type: type,
    status: status,
    page: window.location.pathname
  });
}

/**
 * Track feature usage
 * @param {string} feature - Feature name
 * @param {Object} properties - Additional properties
 */
export function trackFeatureUsage(feature, properties = {}) {
  if (!isInitialized) return;

  posthog.capture('feature_used', {
    feature: feature,
    ...properties
  });
}

/**
 * Track sound toggle with detailed context
 * Sends separate events for enable/disable to understand user behavior
 * @param {boolean} newState - New sound state (true = enabled, false = disabled)
 * @param {string} soundType - Type of sound ('whale_trades' or 'liquidations')
 * @param {Object} context - Additional context
 */
export function trackSoundToggle(newState, soundType, context = {}) {
  if (!isInitialized) return;

  const eventName = newState ? 'sound_enabled' : 'sound_disabled';
  const sessionDuration = Math.round((Date.now() - sessionStart) / 1000);

  posthog.capture(eventName, {
    sound_type: soundType,
    page: window.location.pathname,
    session_duration_seconds: sessionDuration,
    is_first_visit: !localStorage.getItem('crypnal_returning_user'),
    ...context
  });

  // Also track generic feature_used for backwards compatibility
  posthog.capture('feature_used', {
    feature: 'sound_toggle',
    type: soundType,
    enabled: newState
  });

  // Mark user as returning after first interaction
  if (!localStorage.getItem('crypnal_returning_user')) {
    localStorage.setItem('crypnal_returning_user', 'true');
  }
}

/**
 * Track trade interactions
 * @param {string} action - Action type (e.g., 'view_details', 'share', 'copy_address')
 * @param {Object} tradeData - Trade data
 */
export function trackTradeInteraction(action, tradeData = {}) {
  if (!isInitialized) return;

  posthog.capture('trade_interaction', {
    action: action,
    symbol: tradeData.symbol,
    exchange: tradeData.exchange,
    trade_type: tradeData.isLiquidation ? 'liquidation' : 'whale_trade',
    dollar_size: tradeData.dollarSize,
    ...tradeData
  });
}

/**
 * Track tab changes within a page
 * @param {string} tabName - Name of the tab
 * @param {string} page - Page where tab is located
 */
export function trackTabChange(tabName, page) {
  if (!isInitialized) return;

  posthog.capture('tab_changed', {
    tab: tabName,
    page: page
  });
}

/**
 * Track external link clicks
 * @param {string} url - URL that was clicked
 * @param {string} label - Label/description of the link
 */
export function trackExternalLinkClick(url, label) {
  if (!isInitialized) return;

  posthog.capture('external_link_clicked', {
    url: url,
    label: label,
    page: window.location.pathname
  });
}

/**
 * Track language change
 * @param {string} oldLang - Previous language
 * @param {string} newLang - New language
 */
export function trackLanguageChange(oldLang, newLang) {
  if (!isInitialized) return;

  posthog.capture('language_changed', {
    from_language: oldLang,
    to_language: newLang
  });
}

/**
 * Track time spent on a page (call on page leave)
 * @param {string} pageName - Name of the page
 * @param {number} timeSpentMs - Time spent in milliseconds
 */
export function trackTimeOnPage(pageName, timeSpentMs) {
  if (!isInitialized) return;

  posthog.capture('time_on_page', {
    page: pageName,
    time_spent_seconds: Math.round(timeSpentMs / 1000),
    time_spent_ms: timeSpentMs
  });
}

/**
 * Identify user with additional properties
 * @param {string} userId - User ID
 * @param {Object} properties - User properties
 */
export function identifyUser(userId, properties = {}) {
  if (!isInitialized) return;

  posthog.identify(userId, properties);
}

/**
 * Reset user identity (e.g., on logout)
 */
export function resetUser() {
  if (!isInitialized) return;

  posthog.reset();
}

/**
 * Set user properties without identifying
 * @param {Object} properties - Properties to set
 */
export function setUserProperties(properties) {
  if (!isInitialized) return;

  posthog.people.set(properties);
}

/**
 * Get current distinct ID
 * @returns {string|null} Current user ID
 */
export function getDistinctId() {
  if (!isInitialized) return null;

  return posthog.get_distinct_id();
}

// ============================================
// FEATURE FLAGS
// ============================================

/**
 * Check if a feature flag is enabled
 * @param {string} flag - Feature flag name
 * @returns {boolean} Whether the flag is enabled
 */
export function isFeatureEnabled(flag) {
  return isInitialized ? posthog.isFeatureEnabled(flag) : false;
}

/**
 * Get a feature flag value (for multivariate flags)
 * @param {string} flag - Feature flag name
 * @returns {string|boolean|undefined} The flag value
 */
export function getFeatureFlag(flag) {
  return isInitialized ? posthog.getFeatureFlag(flag) : null;
}

// ============================================
// EXISTING-USER DETECTION
// ============================================
// Used by feature-launch announcements (e.g. NewFeatureToast) to avoid
// pestering brand-new users with "we added something new" callouts —
// they have no point of reference for what changed. The flag is a plain
// localStorage key; no server roundtrip and works offline.
//
// Two signals:
//   - `crypnal_returning_user` (legacy) — was set when a user toggled
//     sound. We honour it for backwards compat so existing engaged users
//     are recognised on first load after this code ships.
//   - `tm.firstSeenAt` (new) — UTC ms timestamp of first App mount we
//     observed. Lets future announcements compare against deploy date.

const FIRST_SEEN_KEY = 'tm.firstSeenAt';
const LEGACY_RETURNING_KEY = 'crypnal_returning_user';

function readLocalStorage(key) {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeLocalStorage(key, value) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, value);
  } catch {
    // private browsing / quota exceeded — silent
  }
}

/**
 * Stamp first-seen timestamp on the very first call. Idempotent on
 * subsequent calls. Should be invoked once on App mount, AFTER
 * isExistingUser() has been read for the current pageload (otherwise
 * brand-new users would be classified as existing on their second
 * pageview within the same session).
 */
export function markUserSeen() {
  if (!readLocalStorage(FIRST_SEEN_KEY)) {
    writeLocalStorage(FIRST_SEEN_KEY, String(Date.now()));
  }
}

/**
 * True if the user has visited tradermap.io before. Used to gate
 * "we added X" announcements — new visitors get nothing extra.
 *
 * The legacy `crypnal_returning_user` flag is honoured so engaged
 * pre-existing users light up immediately on first load after this
 * ships, even though their `tm.firstSeenAt` won't exist yet.
 */
export function isExistingUser() {
  if (readLocalStorage(LEGACY_RETURNING_KEY)) return true;
  if (readLocalStorage(FIRST_SEEN_KEY)) return true;
  return false;
}

// ============================================
// FRUSTRATION TRACKING
// ============================================

let clickHistory = [];
const RAGE_THRESHOLD = 3;
const RAGE_WINDOW = 1000;

/**
 * Set up frustration tracking (rage clicks, dead clicks)
 * Call once on app startup after analytics init
 */
export function setupFrustrationTracking() {
  document.addEventListener('click', (e) => {
    if (!isInitialized) return;

    const now = Date.now();
    clickHistory = clickHistory.filter(c => now - c.time < RAGE_WINDOW);
    clickHistory.push({ time: now, target: e.target });

    const sameClicks = clickHistory.filter(c => c.target === e.target);
    if (sameClicks.length >= RAGE_THRESHOLD) {
      posthog.capture('user_frustration', {
        type: 'rage_click',
        count: sameClicks.length,
        element: e.target.tagName,
        element_class: e.target.className,
        page: window.location.pathname
      });
      clickHistory = [];
    }
  });

  console.log('[Analytics] Frustration tracking installed');
}

// ============================================
// FUNNEL & JOURNEY TRACKING
// ============================================

let sessionStart = Date.now();

/**
 * Track entry point for funnel analysis
 */
export function trackEntryPoint() {
  if (!isInitialized) return;

  const params = new URLSearchParams(window.location.search);

  posthog.capture('session_start', {
    entry_page: window.location.pathname,
    referrer: document.referrer,
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_content: params.get('utm_content'),
    utm_term: params.get('utm_term')
  });

  sessionStart = Date.now();
}

/**
 * Track landing page funnel steps
 * @param {string} step - Funnel step name
 * @param {Object} props - Additional properties
 */
export function trackLandingFunnel(step, props = {}) {
  if (!isInitialized) return;

  posthog.capture('landing_funnel', {
    step,
    time_on_page_seconds: Math.round((Date.now() - sessionStart) / 1000),
    ...props
  });
}

/**
 * Track feature discovery (first time user interacts with a feature)
 * @param {string} feature - Feature name
 * @param {string} action - Action taken
 */
export function trackFeatureDiscovery(feature, action) {
  if (!isInitialized) return;

  posthog.capture('feature_discovery', {
    feature,
    action,
    page: window.location.pathname
  });
}

/**
 * Track scroll depth
 * @param {number} depth - Scroll depth percentage (0-100)
 * @param {string} page - Page name
 */
export function trackScrollDepth(depth, page) {
  if (!isInitialized) return;

  posthog.capture('scroll_depth', {
    depth_percent: depth,
    page: page || window.location.pathname
  });
}

/**
 * Track search queries
 * @param {string} query - Search query
 * @param {Object} context - Additional context (resultsCount, page, etc.)
 */
export function trackSearch(query, context = {}) {
  if (!isInitialized) return;

  posthog.capture('search', {
    query: query,
    results_count: context.resultsCount,
    page: context.page || window.location.pathname,
    ...context
  });
}

/**
 * Track wallet interactions (Hyperliquid)
 * @param {string} action - Action type (e.g., 'search_profile', 'view_position', 'copy_address')
 * @param {string} walletAddress - Wallet address (will be truncated for privacy)
 * @param {Object} context - Additional context
 */
export function trackWalletInteraction(action, walletAddress, context = {}) {
  if (!isInitialized) return;

  posthog.capture('wallet_interaction', {
    action: action,
    wallet: walletAddress ? `${walletAddress.slice(0, 10)}...` : null,
    page: context.page || window.location.pathname,
    ...context
  });
}

/**
 * Track external/outbound link clicks
 * @param {string} url - URL that was clicked
 * @param {string} label - Label/description of the link
 * @param {Object} context - Additional context
 */
export function trackOutboundClick(url, label, context = {}) {
  if (!isInitialized) return;

  posthog.capture('outbound_click', {
    url: url,
    label: label,
    page: context.page || window.location.pathname,
    ...context
  });
}

// ============================================
// PAGE ENGAGEMENT TIME TRACKING
// ============================================
// Tracks active vs idle vs hidden time - sends ONE event per page (on leave)
// Cost: +1 event per pageview (very economical)

const engagementTracker = {
  pageEnteredAt: null,
  lastActivityAt: null,
  visibleTime: 0,
  hiddenTime: 0,
  activeTime: 0,
  idleTime: 0,
  isVisible: true,
  isActive: true,
  lastVisibilityChange: null,
  lastActiveChange: null,
  idleTimeout: null,
  currentPage: null,
  IDLE_THRESHOLD: 30000, // 30 sec without activity = idle
  isTracking: false,

  start(page) {
    if (this.isTracking) {
      this.flush(); // Flush previous page data
    }

    const now = Date.now();
    this.pageEnteredAt = now;
    this.lastActivityAt = now;
    this.lastVisibilityChange = now;
    this.lastActiveChange = now;
    this.visibleTime = 0;
    this.hiddenTime = 0;
    this.activeTime = 0;
    this.idleTime = 0;
    this.isVisible = !document.hidden;
    this.isActive = true;
    this.currentPage = page || window.location.pathname;
    this.isTracking = true;

    this.resetIdleTimer();
  },

  recordActivity() {
    if (!this.isTracking) return;

    const now = Date.now();
    this.lastActivityAt = now;

    // If was idle, switch to active
    if (!this.isActive) {
      this.idleTime += now - this.lastActiveChange;
      this.lastActiveChange = now;
      this.isActive = true;
    }

    this.resetIdleTimer();
  },

  resetIdleTimer() {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }
    this.idleTimeout = setTimeout(() => {
      if (this.isTracking && this.isActive) {
        const now = Date.now();
        this.activeTime += now - this.lastActiveChange;
        this.lastActiveChange = now;
        this.isActive = false;
      }
    }, this.IDLE_THRESHOLD);
  },

  handleVisibilityChange() {
    if (!this.isTracking) return;

    const now = Date.now();
    const wasVisible = this.isVisible;
    this.isVisible = !document.hidden;

    if (wasVisible && !this.isVisible) {
      // Page went to background
      this.visibleTime += now - this.lastVisibilityChange;
      if (this.isActive) {
        this.activeTime += now - this.lastActiveChange;
        this.lastActiveChange = now;
      }
    } else if (!wasVisible && this.isVisible) {
      // Page came to foreground
      this.hiddenTime += now - this.lastVisibilityChange;
    }

    this.lastVisibilityChange = now;
  },

  flush() {
    if (!this.isTracking || !this.pageEnteredAt) return;

    const now = Date.now();

    // Finalize current periods
    if (this.isVisible) {
      this.visibleTime += now - this.lastVisibilityChange;
    } else {
      this.hiddenTime += now - this.lastVisibilityChange;
    }

    if (this.isActive) {
      this.activeTime += now - this.lastActiveChange;
    } else {
      this.idleTime += now - this.lastActiveChange;
    }

    const totalTime = now - this.pageEnteredAt;

    // Only send if meaningful time spent (> 1 sec)
    if (totalTime > 1000 && isInitialized) {
      const activeTimeSec = Math.round(this.activeTime / 1000);

      posthog.capture('page_engagement', {
        page: this.currentPage,
        total_time_sec: Math.round(totalTime / 1000),
        visible_time_sec: Math.round(this.visibleTime / 1000),
        hidden_time_sec: Math.round(this.hiddenTime / 1000),
        active_time_sec: activeTimeSec,
        idle_time_sec: Math.round(this.idleTime / 1000),
        engagement_ratio: Math.round((this.activeTime / Math.max(this.visibleTime, 1)) * 100), // % of visible time that was active
      });

      // Accumulate active time for feedback eligibility
      if (activeTimeSec > 0) {
        const prev = parseInt(localStorage.getItem('crypnal_total_active_seconds') || '0', 10);
        const updated = prev + activeTimeSec;
        localStorage.setItem('crypnal_total_active_seconds', String(updated));

        const totalMinutes = Math.round(updated / 60);
        posthog.people.set({ total_active_minutes: totalMinutes });

        // Fire milestone event once at 20 min threshold
        if (prev < 1200 && updated >= 1200) {
          posthog.capture('engaged_user_milestone', { total_active_seconds: updated });
          localStorage.setItem('crypnal_feedback_eligible', 'true');
        }
      }
    }

    // Reset
    this.isTracking = false;
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
    }
  }
};

/**
 * Start tracking page engagement time
 * Call this on page/route change
 * @param {string} page - Page name/path
 */
export function startEngagementTracking(page) {
  engagementTracker.start(page);
}

/**
 * Set up engagement tracking listeners
 * Call once after analytics init
 */
export function setupEngagementTracking() {
  // Throttled activity handler (max once per 2 sec to save CPU)
  let activityThrottled = false;
  const handleActivity = () => {
    if (activityThrottled) return;
    activityThrottled = true;
    engagementTracker.recordActivity();
    setTimeout(() => { activityThrottled = false; }, 2000);
  };

  // Track user activity (throttled)
  document.addEventListener('mousemove', handleActivity, { passive: true });
  document.addEventListener('scroll', handleActivity, { passive: true });
  document.addEventListener('keydown', handleActivity, { passive: true });
  document.addEventListener('click', handleActivity, { passive: true });
  document.addEventListener('touchstart', handleActivity, { passive: true });

  // Track visibility changes
  document.addEventListener('visibilitychange', () => {
    engagementTracker.handleVisibilityChange();
  });

  // Flush on page leave
  window.addEventListener('beforeunload', () => {
    engagementTracker.flush();
  });

  // Also flush when visibility goes hidden (mobile browsers)
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      engagementTracker.flush();
    }
  });

  console.log('[Analytics] Engagement tracking installed');
}

/**
 * Check if user is eligible for feedback survey (20+ min active, not dismissed)
 */
export function isEligibleForFeedback() {
  return (
    localStorage.getItem('crypnal_feedback_eligible') === 'true' &&
    localStorage.getItem('crypnal_feedback_dismissed') !== 'true'
  );
}

/**
 * Mark feedback survey as dismissed so it never shows again
 */
export function markFeedbackDismissed() {
  localStorage.setItem('crypnal_feedback_dismissed', 'true');
}

/**
 * Get total accumulated active seconds
 */
export function getTotalActiveSeconds() {
  return parseInt(localStorage.getItem('crypnal_total_active_seconds') || '0', 10);
}

// Export posthog instance for advanced usage
export { posthog };

// Default export with all tracking functions
export default {
  initAnalytics,
  trackPageView,
  trackFilterChange,
  trackCoinClick,
  trackModalOpen,
  trackModalClose,
  trackWebSocketConnection,
  trackFeatureUsage,
  trackSoundToggle,
  trackTradeInteraction,
  trackTabChange,
  trackExternalLinkClick,
  trackLanguageChange,
  trackTimeOnPage,
  identifyUser,
  resetUser,
  setUserProperties,
  getDistinctId,
  getStoredUserId,
  captureException,
  setInitialUserProperties,
  isFeatureEnabled,
  getFeatureFlag,
  setupFrustrationTracking,
  trackEntryPoint,
  trackLandingFunnel,
  trackFeatureDiscovery,
  trackScrollDepth,
  trackSearch,
  trackWalletInteraction,
  trackOutboundClick,
  startEngagementTracking,
  setupEngagementTracking,
  isEligibleForFeedback,
  markFeedbackDismissed,
  getTotalActiveSeconds,
  posthog
};
